import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  CaretLeft, CaretRight, Plus, Printer, ClipboardText, Trash, PencilLine, Check, X,
  Warning, Archive, ArrowCounterClockwise, CalendarCheck, Copy, UsersThree, SelectionAll,
} from "@phosphor-icons/react";
import { startOfWeek, addDays, format, startOfMonth, endOfMonth, addMonths } from "date-fns";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable,
} from "@dnd-kit/core";
import api, { apiError, API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const SECTIONS = ["Floor", "Bar", "Kitchen", "Scullery", "Reservations", "Host"];
const DEPT_OF = {
  foh_senior: "FOH", foh_junior: "FOH", reservations: "FOH",
  kitchen_senior: "Kitchen", kitchen_junior: "Kitchen", scullery: "Kitchen",
};
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const CONFLICT_LABELS = { leave: "Approved leave", overlap: "Overlapping shift", over_cap: "Over weekly hours cap" };
const key = (empId, day) => `${empId}|${day}`;

const empSection = (emp) => {
  const tc = emp?.tip_category || "";
  if (tc.startsWith("kitchen")) return "Kitchen";
  if (tc === "scullery") return "Scullery";
  return "Floor";
};

const TIME_OPTIONS = [];
for (let h = 6; h <= 23; h++) {
  for (const m of ["00", "30"]) TIME_OPTIONS.push(`${String(h).padStart(2, "0")}:${m}`);
}

function TimeSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-64">
        {TIME_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function ColourPicker({ value, onChange, testid }) {
  return (
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="h-9 w-12 cursor-pointer rounded-md border border-border glass p-1" title="Grid colour" />
  );
}

function TemplatesDialog({ open, onClose, templates, reload }) {
  const [add, setAdd] = useState({ name: "", start: "11:00", end: "16:00", colour: "#A78BFA" });
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({ name: "", start: "", end: "", colour: "#A78BFA", archived: false });

  const create = async () => {
    if (!add.name.trim()) return toast.error("Preset name required");
    try {
      await api.post("/shift-templates", add);
      toast.success("Preset saved");
      setAdd({ name: "", start: "11:00", end: "16:00", colour: "#A78BFA" });
      reload();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/shift-templates/${editId}`, edit);
      toast.success("Preset updated");
      setEditId(null);
      reload();
    } catch (e) {
      toast.error(apiError(e, "Update failed"));
    }
  };

  const toggleArchive = async (t) => {
    try {
      await api.put(`/shift-templates/${t.id}`, { name: t.name, start: t.start, end: t.end, colour: t.colour || "#A78BFA", archived: !t.archived });
      toast.success(t.archived ? `Preset "${t.name}" restored` : `Preset "${t.name}" archived`);
      reload();
    } catch (e) {
      toast.error(apiError(e, "Update failed"));
    }
  };

  const remove = async (t) => {
    try {
      await api.delete(`/shift-templates/${t.id}`);
      toast.success(`Preset "${t.name}" deleted`);
      reload();
    } catch (e) {
      toast.error(apiError(e, "Delete failed"));
    }
  };

  const active = templates.filter((t) => !t.archived);
  const archived = templates.filter((t) => t.archived);

  const row = (t) => (
    <div key={t.id} className={`flex items-center gap-2 border-b last:border-0 pb-2 ${t.archived ? "opacity-50" : ""}`} data-testid={`template-row-${t.id}`}>
      {editId === t.id ? (
        <>
          <ColourPicker testid={`template-edit-colour-${t.id}`} value={edit.colour} onChange={(v) => setEdit({ ...edit, colour: v })} />
          <Input className="h-8 flex-1" data-testid={`template-edit-name-${t.id}`} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
          <TimeSelect testid={`template-edit-start-${t.id}`} value={edit.start} onChange={(v) => setEdit({ ...edit, start: v })} />
          <TimeSelect testid={`template-edit-end-${t.id}`} value={edit.end} onChange={(v) => setEdit({ ...edit, end: v })} />
          <Button size="sm" variant="ghost" data-testid={`template-edit-save-${t.id}`} onClick={saveEdit}><Check size={16} /></Button>
          <Button size="sm" variant="ghost" onClick={() => setEditId(null)}><X size={16} /></Button>
        </>
      ) : (
        <>
          <span className="h-4 w-4 rounded-sm border border-black/10 shrink-0" style={{ backgroundColor: t.colour || "#A78BFA" }} data-testid={`template-swatch-${t.id}`} />
          <span className="flex-1 text-sm font-medium">{t.name}</span>
          <span className="text-sm text-muted-foreground num">{t.start}–{t.end}</span>
          {t.archived && <Badge variant="outline" className="text-[10px]">Archived</Badge>}
          <Button size="sm" variant="ghost" data-testid={`template-edit-${t.id}`}
            onClick={() => { setEditId(t.id); setEdit({ name: t.name, start: t.start, end: t.end, colour: t.colour || "#A78BFA", archived: !!t.archived }); }}>
            <PencilLine size={15} />
          </Button>
          <Button size="sm" variant="ghost" data-testid={`template-archive-${t.id}`} onClick={() => toggleArchive(t)}
            title={t.archived ? "Restore preset" : "Archive preset"}>
            {t.archived ? <ArrowCounterClockwise size={15} /> : <Archive size={15} />}
          </Button>
          <Button size="sm" variant="ghost" data-testid={`template-delete-${t.id}`} onClick={() => remove(t)}
            className="text-destructive hover:text-destructive">
            <Trash size={15} />
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="templates-dialog">
        <DialogHeader><DialogTitle className="font-heading">Shift preset library</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {templates.length === 0 && <p className="text-sm text-muted-foreground">No presets yet — add one below.</p>}
          {active.map(row)}
          {archived.length > 0 && <p className="text-xs text-muted-foreground pt-2">Archived</p>}
          {archived.map(row)}
        </div>
        <div className="border-t pt-3 space-y-2">
          <p className="text-xs text-muted-foreground">New preset</p>
          <div className="flex items-end gap-2 flex-wrap">
            <ColourPicker testid="template-add-colour" value={add.colour} onChange={(v) => setAdd({ ...add, colour: v })} />
            <div className="flex-1 min-w-[120px] space-y-1">
              <Label className="text-xs">Name</Label>
              <Input className="h-9" placeholder="e.g. Lunch" data-testid="template-add-name" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} />
            </div>
            <div className="space-y-1"><Label className="text-xs">Start</Label><TimeSelect testid="template-add-start" value={add.start} onChange={(v) => setAdd({ ...add, start: v })} /></div>
            <div className="space-y-1"><Label className="text-xs">End</Label><TimeSelect testid="template-add-end" value={add.end} onChange={(v) => setAdd({ ...add, end: v })} /></div>
            <Button className="h-9 bg-primary hover:bg-[#8B5CF6] text-white" data-testid="template-add-btn" onClick={create}>Add</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PatternDialog({ open, onClose, employees, templates, patterns, reloadPatterns }) {
  const [empId, setEmpId] = useState("");
  const [pat, setPat] = useState({});
  const active = templates.filter((t) => !t.archived);

  useEffect(() => {
    if (!empId) return;
    const existing = patterns.find((p) => p.employee_id === empId);
    setPat(existing?.pattern || {});
  }, [empId, patterns]);

  const save = async () => {
    try {
      await api.put(`/staff-patterns/${empId}`, { pattern: pat });
      toast.success("Weekly pattern saved");
      reloadPatterns();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="pattern-dialog">
        <DialogHeader><DialogTitle className="font-heading">Recurring weekly pattern</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Employee</Label>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger data-testid="pattern-employee-select"><SelectValue placeholder="Choose employee…" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id} data-testid={`pattern-employee-${e.id}`}>{e.first_name} {e.last_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {empId && (
            <div className="space-y-2" data-testid="pattern-days">
              {WEEKDAYS.map((d, i) => (
                <div key={d} className="flex items-center gap-3">
                  <span className="w-10 text-sm font-medium">{d}</span>
                  <Select value={pat[String(i)] || "off"} onValueChange={(v) => setPat((p) => { const n = { ...p }; if (v === "off") delete n[String(i)]; else n[String(i)] = v; return n; })}>
                    <SelectTrigger className="h-8" data-testid={`pattern-day-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="off">Off</SelectItem>
                      {active.map((t) => (
                        <SelectItem key={t.id} value={t.id} data-testid={`pattern-day-${i}-${t.id}`}>
                          <span className="flex items-center gap-2">
                            <span className="h-3 w-3 rounded-sm inline-block" style={{ backgroundColor: t.colour || "#A78BFA" }} />
                            {t.name} · {t.start}–{t.end}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <Button onClick={save} disabled={!empId} data-testid="pattern-save" className="bg-primary hover:bg-[#8B5CF6] text-white">Save pattern</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PresetChip({ t }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `preset-${t.id}`, data: { type: "preset", template: t } });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} data-testid={`preset-${t.id}`}
      className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs cursor-grab active:cursor-grabbing touch-none select-none glass transition-opacity ${isDragging ? "opacity-40" : ""}`}
      style={{ borderColor: `${t.colour || "#A78BFA"}80` }}>
      <span className="h-3 w-3 rounded-sm shrink-0" style={{ backgroundColor: t.colour || "#A78BFA" }} />
      <span className="font-medium truncate">{t.name}</span>
      <span className="text-muted-foreground num whitespace-nowrap">{t.start}–{t.end}</span>
    </div>
  );
}

function ShiftChip({ s, colour, onClick }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `shift-${s.uid}`, data: { type: "shift", shift: s } });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid={`shift-chip-${s.uid}`}
      title={`${s.start}–${s.end} · ${s.section}${s.conflicts?.length ? ` · Conflicts: ${s.conflicts.map((c) => CONFLICT_LABELS[c]).join(", ")}` : ""}`}
      className={`rounded border px-1.5 py-1 text-[11px] leading-tight cursor-grab active:cursor-grabbing touch-none select-none transition-opacity hover:brightness-95 ${isDragging ? "opacity-40" : ""}`}
      style={{ backgroundColor: `${colour}1F`, borderColor: `${colour}66` }}>
      <span className="font-medium num flex items-center gap-1">
        {s.start}–{s.end}
        {s.conflicts?.length > 0 && <Warning size={12} weight="fill" className="text-[#F87171] shrink-0" data-testid={`conflict-icon-${s.uid}`} />}
      </span>
      <span className="block text-muted-foreground truncate">{s.section}</span>
    </div>
  );
}

function RosterCell({ emp, iso, shifts, tplById, selectMode, selected, onToggleSelect, onOpenCell, onOpenShift }) {
  const { setNodeRef, isOver } = useDroppable({ id: `cell-${emp.id}|${iso}`, data: { empId: emp.id, iso } });
  return (
    <td className="px-1.5 py-1.5 align-top">
      <div ref={setNodeRef}
        data-testid={`roster-cell-${emp.id}-${iso}`}
        onClick={() => (selectMode ? onToggleSelect() : onOpenCell())}
        className={`w-full min-h-[56px] rounded-md border px-1 py-1 transition-colors duration-150 text-left space-y-1 ${
          selected ? "ring-2 ring-[#A78BFA] border-[#A78BFA]/50 bg-[#A78BFA]/5"
            : isOver ? "ring-2 ring-[#4A5D23] border-[#4A5D23]/50 bg-[#4A5D23]/5"
            : shifts.length ? "border-border hover:bg-accent/60" : "border-dashed border-border hover:bg-accent text-muted-foreground"
        }`}>
        {shifts.length === 0 && (
          <span className="flex items-center gap-1 justify-center text-xs text-muted-foreground py-2"><Plus size={12} /> Off</span>
        )}
        {shifts.map((s) => (
          <ShiftChip key={s.uid} s={s} colour={(s.template_id && tplById[s.template_id]?.colour) || "#A78BFA"} onClick={() => onOpenShift(s)} />
        ))}
      </div>
    </td>
  );
}

function MonthGrid({ monthDate, cal }) {
  const mStart = startOfMonth(monthDate);
  const gridStart = startOfWeek(mStart, { weekStartsOn: 1 });
  const gridEnd = addDays(startOfWeek(endOfMonth(monthDate), { weekStartsOn: 1 }), 6);
  const weeks = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 7)) {
    weeks.push(Array.from({ length: 7 }, (_, i) => addDays(d, i)));
  }
  const todayIso = format(new Date(), "yyyy-MM-dd");
  return (
    <div className="glass border border-border rounded-lg overflow-hidden" data-testid="month-grid">
      <div className="grid grid-cols-7 border-b text-xs text-muted-foreground">
        {WEEKDAYS.map((d) => <div key={d} className="px-2 py-2 font-medium">{d}</div>)}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b last:border-0">
          {week.map((d) => {
            const iso = format(d, "yyyy-MM-dd");
            const inMonth = d.getMonth() === monthDate.getMonth();
            const shifts = (cal?.shifts || []).filter((s) => s.date === iso);
            const leaves = (cal?.leave || []).filter((l) => l.start_date <= iso && l.end_date >= iso);
            const hol = cal?.holidays?.[iso];
            return (
              <div key={iso} data-testid={`month-cell-${iso}`}
                className={`min-h-[110px] border-r last:border-r-0 p-1.5 align-top ${inMonth ? "" : "bg-muted/40"} ${iso === todayIso ? "bg-[#A78BFA]/5" : ""}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${inMonth ? "" : "text-muted-foreground/50"}`}>{d.getDate()}</span>
                  {hol && <span className="text-[9px] text-[#F87171] leading-tight text-right">{hol}</span>}
                </div>
                <div className="mt-1 space-y-0.5">
                  {shifts.slice(0, 3).map((s) => (
                    <div key={s.id} className="text-[10px] leading-tight bg-[#A78BFA]/10 rounded px-1 py-0.5 truncate" title={`${s.employee_name} ${s.start}–${s.end} ${s.section}`}>
                      {s.employee_name.split(" ")[0]} {s.start}–{s.end}
                    </div>
                  ))}
                  {shifts.length > 3 && <div className="text-[9px] text-muted-foreground">+{shifts.length - 3} more shifts</div>}
                  {leaves.slice(0, 2).map((l) => (
                    <div key={l.id} className={`text-[10px] leading-tight rounded px-1 py-0.5 truncate ${l.status === "approved" ? "bg-[#D99A29]/20" : "bg-[#D99A29]/10 border border-dashed border-[#D99A29]/40"}`}>
                      {l.employee_name.split(" ")[0]} — {l.type}{l.status === "pending" ? " (pending)" : ""}
                    </div>
                  ))}
                  {leaves.length > 2 && <div className="text-[9px] text-muted-foreground">+{leaves.length - 2} more leave</div>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function YearGrid({ year, cal, onPickMonth }) {
  const todayIso = format(new Date(), "yyyy-MM-dd");
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="year-grid">
      {Array.from({ length: 12 }, (_, m) => {
        const first = new Date(year, m, 1);
        const offset = (getMondayIndex(first));
        const daysInMonth = endOfMonth(first).getDate();
        const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
        return (
          <button key={m} onClick={() => onPickMonth(first)} data-testid={`year-month-${m}`}
            className="glass border border-border rounded-lg p-3 text-left hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200">
            <p className="font-heading font-semibold text-sm mb-2">{format(first, "MMMM")}</p>
            <div className="grid grid-cols-7 gap-0.5">
              {["M","T","W","T","F","S","S"].map((d, i) => <span key={i} className="text-[8px] text-muted-foreground text-center">{d}</span>)}
              {cells.map((d, i) => {
                if (!d) return <span key={i} />;
                const iso = format(new Date(year, m, d), "yyyy-MM-dd");
                const hasShift = cal?.shifts?.some((s) => s.date === iso);
                const hasLeave = cal?.leave?.some((l) => l.start_date <= iso && l.end_date >= iso);
                const hol = cal?.holidays?.[iso];
                return (
                  <span key={i} title={`${iso}${hol ? ` · ${hol}` : ""}${hasShift ? " · shifts" : ""}${hasLeave ? " · leave" : ""}`}
                    className={`text-[9px] text-center rounded-sm py-0.5 ${
                      iso === todayIso ? "ring-1 ring-[#A78BFA]" : ""
                    } ${hasLeave ? "bg-[#D99A29]/40" : hasShift ? "bg-[#A78BFA]/25" : ""} ${hol ? "text-[#F87171] font-semibold" : ""}`}>
                    {d}
                  </span>
                );
              })}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function getMondayIndex(d) {
  return (d.getDay() + 6) % 7;
}

export default function Roster() {
  const [view, setView] = useState("week");
  const [dept, setDept] = useState("all");
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [monthDate, setMonthDate] = useState(new Date());
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [cal, setCal] = useState(null);
  const [shiftMap, setShiftMap] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ start: "10:00", end: "18:00", section: "Floor" });
  const [tplId, setTplId] = useState("custom");
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [patternOpen, setPatternOpen] = useState(false);
  const [patterns, setPatterns] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState({});
  const [bulkTpl, setBulkTpl] = useState("");
  const [applyScope, setApplyScope] = useState("week");
  const [applyOpen, setApplyOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const uidRef = useRef(1);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const wsStr = format(weekStart, "yyyy-MM-dd");
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const load = useCallback(() =>
    api.get(`/roster?week_start=${wsStr}`).then((r) => {
      setData(r.data);
      const m = {};
      r.data.shifts.forEach((s) => {
        const k = key(s.employee_id, s.date);
        (m[k] = m[k] || []).push({ ...s, uid: s.id });
      });
      setShiftMap(m);
      setSelected({});
    }).catch(() => toast.error("Failed to load roster")), [wsStr]);

  const loadPatterns = useCallback(() => api.get("/staff-patterns").then((r) => setPatterns(r.data)).catch(() => {}), []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPatterns(); }, [loadPatterns]);

  useEffect(() => {
    if (view === "week") return;
    let s, e;
    if (view === "month") {
      const ms = startOfMonth(monthDate);
      s = format(startOfWeek(ms, { weekStartsOn: 1 }), "yyyy-MM-dd");
      e = format(addDays(startOfWeek(endOfMonth(ms), { weekStartsOn: 1 }), 6), "yyyy-MM-dd");
    } else {
      s = `${year}-01-01`; e = `${year}-12-31`;
    }
    api.get(`/calendar?start=${s}&end=${e}`).then((r) => setCal(r.data)).catch(() => toast.error("Failed to load calendar"));
  }, [view, monthDate, year]);

  const activeTemplates = useMemo(() => (data?.templates || []).filter((t) => !t.archived), [data]);
  const tplById = useMemo(() => Object.fromEntries((data?.templates || []).map((t) => [t.id, t])), [data]);

  const newUid = () => `loc-${uidRef.current++}`;

  const appendShift = (empId, iso, partial) => {
    setShiftMap((m) => {
      const k = key(empId, iso);
      const arr = m[k] || [];
      if (arr.some((x) => x.start === partial.start && x.end === partial.end)) return m;
      return { ...m, [k]: [...arr, { uid: newUid(), employee_id: empId, date: iso, section: "Floor", ...partial }] };
    });
  };

  const openCell = (empId, day) => {
    const d = typeof day === "string" ? day : format(day, "yyyy-MM-dd");
    const arr = shiftMap[key(empId, d)] || [];
    const existing = arr[0] || null;
    setForm(existing ? { start: existing.start, end: existing.end, section: existing.section } : { start: "10:00", end: "18:00", section: empSection(data.employees.find((e) => e.id === empId)) });
    const match = existing && (data?.templates || []).find((t) => t.start === existing.start && t.end === existing.end);
    setTplId(match ? match.id : "custom");
    setEditing({ empId, day: d, uid: existing?.uid || null });
  };

  const openShift = (s) => {
    setForm({ start: s.start, end: s.end, section: s.section });
    const match = (data?.templates || []).find((t) => t.start === s.start && t.end === s.end);
    setTplId(match ? match.id : "custom");
    setEditing({ empId: s.employee_id, day: s.date, uid: s.uid });
  };

  const applyTemplate = (id) => {
    setTplId(id);
    if (id === "custom") return;
    const t = (data.templates || []).find((x) => x.id === id);
    if (t) setForm((f) => ({ ...f, start: t.start, end: t.end }));
  };

  const saveCell = () => {
    const k = key(editing.empId, editing.day);
    setShiftMap((m) => {
      const arr = [...(m[k] || [])];
      const tpl = tplId !== "custom" ? tplById[tplId] : null;
      if (editing.uid) {
        const i = arr.findIndex((x) => x.uid === editing.uid);
        if (i >= 0) arr[i] = { ...arr[i], ...form, template_id: tpl?.id || null };
      } else {
        arr.push({ uid: newUid(), employee_id: editing.empId, date: editing.day, ...form, template_id: tpl?.id || null });
      }
      return { ...m, [k]: arr };
    });
    setEditing(null);
  };

  const removeCell = () => {
    const k = key(editing.empId, editing.day);
    setShiftMap((m) => {
      const arr = (m[k] || []).filter((x) => x.uid !== editing.uid);
      const n = { ...m };
      if (arr.length) n[k] = arr; else delete n[k];
      return n;
    });
    setEditing(null);
  };

  const saveAll = async () => {
    try {
      const shifts = Object.values(shiftMap).flat().map((s) => ({
        employee_id: s.employee_id, date: s.date, start: s.start, end: s.end, section: s.section,
        template_id: s.template_id || null,
      }));
      await api.put(`/roster/${data.roster.id}/shifts`, { shifts });
      toast.success("Roster saved");
      load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    }
  };

  const publish = async () => {
    try {
      await api.post(`/roster/${data.roster.id}/publish`);
      toast.success("Roster published — staff notified by email and in-app");
      load();
    } catch (e) {
      toast.error(apiError(e, "Publish failed"));
    }
  };

  const onDragEnd = (ev) => {
    const { active, over } = ev;
    if (!over || !over.data.current) return;
    const { empId, iso } = over.data.current;
    const src = active.data.current;
    const emp = data.employees.find((e) => e.id === empId);
    if (src?.type === "preset") {
      appendShift(empId, iso, { start: src.template.start, end: src.template.end, section: empSection(emp), template_id: src.template.id });
    } else if (src?.type === "shift") {
      if (src.shift.employee_id === empId && src.shift.date === iso) return;
      appendShift(empId, iso, { start: src.shift.start, end: src.shift.end, section: src.shift.section, template_id: src.shift.template_id || null });
    }
  };

  const toggleSelect = (empId, iso) => {
    const k = key(empId, iso);
    setSelected((s) => { const n = { ...s }; if (n[k]) delete n[k]; else n[k] = { empId, iso }; return n; });
  };

  const bulkApply = () => {
    const t = tplById[bulkTpl];
    if (!t) return toast.error("Choose a preset first");
    const cells = Object.values(selected);
    cells.forEach(({ empId, iso }) => {
      const emp = data.employees.find((e) => e.id === empId);
      appendShift(empId, iso, { start: t.start, end: t.end, section: empSection(emp), template_id: t.id });
    });
    toast.success(`Preset "${t.name}" applied to ${cells.length} cell(s)`);
    setSelected({});
  };

  const applyPattern = async () => {
    setBusy(true);
    try {
      const r = await api.post("/roster/apply-pattern", { week_start: wsStr, scope: applyScope });
      toast.success(`Pattern applied — ${r.data.created} shift(s) created as draft${r.data.flagged.length ? `, ${r.data.flagged.length} flagged` : ""}`);
      setApplyOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, "Apply failed"));
    } finally {
      setBusy(false);
    }
  };

  const copyWeek = async () => {
    setBusy(true);
    try {
      const r = await api.post("/roster/copy-week", { week_start: wsStr });
      toast.success(`Copied ${r.data.created} shift(s) from week of ${r.data.copied_from} (draft)${r.data.flagged.length ? `, ${r.data.flagged.length} flagged` : ""}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Copy failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <p className="text-muted-foreground">Loading…</p>;
  const published = data.roster.status === "published";
  const rosterEmployees = dept === "all" ? data.employees : data.employees.filter((e) => DEPT_OF[e.tip_category] === dept);
  const allShifts = Object.values(shiftMap).flat();
  const conflictCounts = allShifts.reduce((acc, s) => {
    (s.conflicts || []).forEach((c) => { acc[c] = (acc[c] || 0) + 1; });
    return acc;
  }, {});
  const conflictTotal = Object.values(conflictCounts).reduce((a, b) => a + b, 0);
  const patternCount = patterns.filter((p) => Object.keys(p.pattern || {}).length > 0).length;

  return (
    <div className="space-y-6" data-testid="roster-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Roster</h1>
          <div className="text-muted-foreground mt-1">
            {view === "week" && <>Week of {format(weekStart, "d MMM yyyy")} ·{" "}
              <Badge variant={published ? "default" : "outline"} className={published ? "bg-[#4A5D23] text-white" : ""} data-testid="roster-status">
                {published ? "Published" : "Draft"}
              </Badge></>}
            {view === "month" && format(monthDate, "MMMM yyyy")}
            {view === "year" && year}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Tabs value={view} onValueChange={setView}>
            <TabsList data-testid="roster-view-toggle">
              <TabsTrigger value="week" data-testid="view-week">Week</TabsTrigger>
              <TabsTrigger value="month" data-testid="view-month">Month</TabsTrigger>
              <TabsTrigger value="year" data-testid="view-year">Year</TabsTrigger>
            </TabsList>
          </Tabs>
          {view === "week" && (
            <>
              <div className="flex rounded-md border border-border overflow-hidden" data-testid="dept-filter">
                {[["all", "All"], ["FOH", "FOH"], ["Kitchen", "Kitchen"]].map(([v, label]) => (
                  <button key={v} data-testid={`dept-${v.toLowerCase()}`} onClick={() => setDept(v)}
                    className={`px-3 h-9 text-sm transition-colors ${dept === v ? "bg-primary text-white" : "glass text-foreground hover:bg-accent"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <Button variant="outline" size="sm" data-testid="prev-week" onClick={() => setWeekStart(addDays(weekStart, -7))}><CaretLeft size={16} /></Button>
              <Button variant="outline" size="sm" data-testid="this-week" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }))}>This week</Button>
              <Button variant="outline" size="sm" data-testid="next-week" onClick={() => setWeekStart(addDays(weekStart, 7))}><CaretRight size={16} /></Button>
              <a href={`${API_BASE}/roster/${data.roster.id}/pdf`} target="_blank" rel="noreferrer" data-testid="roster-pdf-btn">
                <Button variant="outline" size="sm"><Printer size={15} className="mr-1" /> PDF</Button>
              </a>
              <Button variant="outline" size="sm" data-testid="templates-btn" onClick={() => setTemplatesOpen(true)}>
                <ClipboardText size={15} className="mr-1" /> Presets
              </Button>
              <Button variant="outline" size="sm" data-testid="patterns-btn" onClick={() => setPatternOpen(true)}>
                <UsersThree size={15} className="mr-1" /> Patterns
              </Button>
              <Button variant="outline" size="sm" data-testid="apply-pattern-btn" onClick={() => setApplyOpen(true)} disabled={patternCount === 0}
                title={patternCount === 0 ? "Set a weekly pattern first (Patterns)" : "Generate draft shifts from patterns"}>
                <CalendarCheck size={15} className="mr-1" /> Apply pattern
              </Button>
              <Button variant="outline" size="sm" data-testid="copy-week-btn" onClick={copyWeek} disabled={busy}>
                <Copy size={15} className="mr-1" /> Copy last week
              </Button>
              <Button onClick={saveAll} variant="outline" data-testid="save-roster-btn">Save</Button>
              <Button onClick={async () => { await saveAll(); await publish(); }} data-testid="publish-roster-btn"
                className="bg-primary hover:bg-[#8B5CF6] text-white">
                {published ? "Save & re-notify" : "Save & publish"}
              </Button>
            </>
          )}
          {view === "month" && (
            <>
              <Button variant="outline" size="sm" data-testid="prev-month" onClick={() => setMonthDate(addMonths(monthDate, -1))}><CaretLeft size={16} /></Button>
              <Button variant="outline" size="sm" onClick={() => setMonthDate(new Date())}>This month</Button>
              <Button variant="outline" size="sm" data-testid="next-month" onClick={() => setMonthDate(addMonths(monthDate, 1))}><CaretRight size={16} /></Button>
            </>
          )}
          {view === "year" && (
            <>
              <Button variant="outline" size="sm" data-testid="prev-year" onClick={() => setYear(year - 1)}><CaretLeft size={16} /></Button>
              <Button variant="outline" size="sm" onClick={() => setYear(new Date().getFullYear())}>This year</Button>
              <Button variant="outline" size="sm" data-testid="next-year" onClick={() => setYear(year + 1)}><CaretRight size={16} /></Button>
            </>
          )}
        </div>
      </div>

      {view === "week" && conflictTotal > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-[#D99A29]/50 bg-[#D99A29]/10 px-3 py-2 text-sm" data-testid="conflict-banner">
          <Warning size={16} weight="fill" className="text-[#8a6414] mt-0.5 shrink-0" />
          <span>
            {conflictTotal} conflict flag(s) this week:
            {conflictCounts.leave ? ` ${conflictCounts.leave}× approved leave ·` : ""}
            {conflictCounts.overlap ? ` ${conflictCounts.overlap}× overlap ·` : ""}
            {conflictCounts.over_cap ? ` ${conflictCounts.over_cap}× over ${data.weekly_hours_cap || 45}h cap ·` : ""}
            {" "}Flags are warnings only — nothing is blocked.
          </span>
        </div>
      )}

      {view === "week" && (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
          <div className="glass border border-border rounded-lg p-3 space-y-2" data-testid="preset-palette">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">Drag a preset onto a cell, or drag an existing shift to copy it. Click a shift to edit times.</p>
              <Button variant={selectMode ? "default" : "outline"} size="sm" data-testid="select-mode-toggle"
                className={selectMode ? "bg-primary text-white hover:bg-[#8B5CF6]" : ""}
                onClick={() => { setSelectMode(!selectMode); setSelected({}); }}>
                <SelectionAll size={15} className="mr-1" /> {selectMode ? "Done selecting" : "Select cells"}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeTemplates.length === 0 && <p className="text-sm text-muted-foreground">No active presets — open Presets to create one.</p>}
              {activeTemplates.map((t) => <PresetChip key={t.id} t={t} />)}
            </div>
            {selectMode && Object.keys(selected).length > 0 && (
              <div className="flex flex-wrap items-center gap-2 border-t pt-2" data-testid="bulk-bar">
                <span className="text-sm font-medium">{Object.keys(selected).length} cell(s) selected</span>
                <Select value={bulkTpl} onValueChange={setBulkTpl}>
                  <SelectTrigger className="h-8 w-52" data-testid="bulk-preset-select"><SelectValue placeholder="Choose preset…" /></SelectTrigger>
                  <SelectContent>
                    {activeTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`bulk-preset-${t.id}`}>{t.name} · {t.start}–{t.end}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={bulkApply} data-testid="bulk-apply-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">Apply to selected</Button>
                <Button size="sm" variant="ghost" data-testid="bulk-clear-btn" onClick={() => setSelected({})}>Clear</Button>
              </div>
            )}
          </div>

          <div className="glass border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium w-44 sticky left-0 glass">Employee</th>
                  {days.map((d) => {
                    const iso = format(d, "yyyy-MM-dd");
                    const hol = data.holidays[iso];
                    return (
                      <th key={iso} className="px-3 py-3 font-medium">
                        <div>{format(d, "EEE d MMM")}</div>
                        {hol && <div className="text-[10px] text-[#F87171] font-normal mt-0.5" data-testid={`holiday-${iso}`}>{hol}</div>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rosterEmployees.map((e) => (
                  <tr key={e.id} className="border-b last:border-0" data-testid={`roster-row-${e.id}`}>
                    <td className="px-4 py-2 font-medium sticky left-0 glass">
                      {e.first_name} {e.last_name}
                      <span className="block text-xs text-muted-foreground font-normal">{e.position}</span>
                    </td>
                    {days.map((d) => {
                      const iso = format(d, "yyyy-MM-dd");
                      return (
                        <RosterCell key={iso} emp={e} iso={iso}
                          shifts={shiftMap[key(e.id, iso)] || []}
                          tplById={tplById}
                          selectMode={selectMode}
                          selected={!!selected[key(e.id, iso)]}
                          onToggleSelect={() => toggleSelect(e.id, iso)}
                          onOpenCell={() => openCell(e.id, iso)}
                          onOpenShift={openShift} />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DragOverlay />
        </DndContext>
      )}

      {view === "month" && (cal ? <MonthGrid monthDate={monthDate} cal={cal} /> : <p className="text-muted-foreground">Loading…</p>)}
      {view === "year" && (cal ? <YearGrid year={year} cal={cal} onPickMonth={(d) => { setMonthDate(d); setView("month"); }} /> : <p className="text-muted-foreground">Loading…</p>)}
      {view !== "week" && (
        <p className="text-xs text-muted-foreground">
          Terracotta = scheduled shifts · Amber = leave (solid = approved, dashed = pending) · Red text = public holiday. Editing happens in Week view.
        </p>
      )}

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-sm" data-testid="shift-dialog">
          <DialogHeader><DialogTitle className="font-heading">Shift — {editing?.day}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Shift preset</Label>
              <Select value={tplId} onValueChange={applyTemplate}>
                <SelectTrigger data-testid="shift-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom times</SelectItem>
                  {activeTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`shift-template-${t.id}`}>{t.name} · {t.start}–{t.end}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Start</Label>
                <TimeSelect testid="shift-start" value={form.start} onChange={(v) => setForm({ ...form, start: v })} />
              </div>
              <div className="space-y-1.5">
                <Label>End</Label>
                <TimeSelect testid="shift-end" value={form.end} onChange={(v) => setForm({ ...form, end: v })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Section / role</Label>
              <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
                <SelectTrigger data-testid="shift-section"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-between">
              {editing?.uid ? (
                <Button variant="outline" onClick={removeCell} data-testid="shift-remove" className="text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive">Remove</Button>
              ) : <span />}
              <Button onClick={saveCell} data-testid="shift-save" className="bg-primary hover:bg-[#8B5CF6] text-white">Set shift</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-sm" data-testid="apply-pattern-dialog">
          <DialogHeader><DialogTitle className="font-heading">Apply weekly patterns</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Generates draft shifts from saved staff patterns. Existing identical shifts are skipped; conflicts are flagged, not blocked.
            </p>
            <div className="space-y-1.5">
              <Label>Scope</Label>
              <Select value={applyScope} onValueChange={setApplyScope}>
                <SelectTrigger data-testid="apply-pattern-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="week" data-testid="apply-scope-week">This week ({format(weekStart, "d MMM")})</SelectItem>
                  <SelectItem value="month" data-testid="apply-scope-month">Entire month ({format(weekStart, "MMMM yyyy")})</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end">
              <Button onClick={applyPattern} disabled={busy} data-testid="apply-pattern-confirm" className="bg-primary hover:bg-[#8B5CF6] text-white">
                Generate draft shifts
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <TemplatesDialog open={templatesOpen} onClose={() => setTemplatesOpen(false)} templates={data.templates || []} reload={load} />
      <PatternDialog open={patternOpen} onClose={() => setPatternOpen(false)} employees={data.employees} templates={data.templates || []} patterns={patterns} reloadPatterns={loadPatterns} />
    </div>
  );
}
